import { useCallback, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { messagesApi, type CombinedMessage, type StreamingSession } from '@/services/messages';
import { MessagesContextValue, MessagesState, StatusEntry, UiMessage } from '@/types/message';
import messagesReducer, { initialBucket } from '@/reducers/messages';
import { StreamingChat, StreamingEvent } from '@/types/streaming';
import { isCombinedMessageLike, keyOf, mapCombinedToUi, pendingKeyOf } from '@/utils/messages';
import { MessagesContext } from '@/contexts/messages';
import { useChatsContext } from '@/contexts/chats';

const initialState: MessagesState = {};

export const MessagesProvider = ({ children }: { children: ReactNode }) => {
  const chats = useChatsContext();
  const [state, dispatch] = useReducer(messagesReducer, initialState);
  const pendingRef = useRef(new Map<string, Promise<UiMessage[]>>());
  const currentActiveStreamRef = useRef<{
    session: StreamingSession;
    cancel: () => void;
    isCancelled: () => boolean;
  } | null>(null);
  const stateRef = useRef(state);

  // Синхронизируем ref с state
  stateRef.current = state;

  const setMessages = useCallback<MessagesContextValue['setMessages']>(
    (accountId, chatId, data) => {
      const key = chatId != null ? keyOf(accountId, chatId) : pendingKeyOf(accountId);
      dispatch({ type: 'set', key, data });
    },
    []
  );

  const load = useCallback<MessagesContextValue['load']>(
    async (accountId, chatId, options) => {
      const key = keyOf(accountId, chatId);
      const shouldForce = options?.force ?? false;
      const existing = stateRef.current[key];

      if (!shouldForce) {
        const pending = pendingRef.current.get(key);
        if (pending) return pending;
        if (existing && (existing.status === 'success' || existing.status === 'loading')) {
          return existing.data;
        }
      } else {
        pendingRef.current.delete(key);
      }

      const request = (async () => {
        dispatch({ type: 'start', key });
        try {
          const rows = await messagesApi.list(accountId, chatId);
          const data = mapCombinedToUi(rows);
          dispatch({ type: 'success', key, data });
          return data;
        } catch (error) {
          const err = error instanceof Error ? error : new Error('Failed to fetch messages');
          dispatch({ type: 'error', key, error: err });
          throw err;
        } finally {
          pendingRef.current.delete(key);
        }
      })();

      pendingRef.current.set(key, request);
      return request;
    },
    []
  );

  const startRegenerate = useCallback<MessagesContextValue['startRegenerate']>(
    async (accountId, chatId, messageId, messageTitle) => {
      const key = keyOf(accountId, chatId);
      const existing = stateRef.current[key]?.data ?? [];
      const currentIndex = existing.findIndex(i => i.id == messageId);
      const deduped = existing
        .map(i => {
          if (i.id != messageId) {
            return i;
          }
          return { ...i, title: messageTitle, isLoading: true };
        })
        .filter((_, id) => id <= currentIndex);
      dispatch({
        type: 'set',
        key,
        data: deduped,
      });
      return messagesApi.remove(accountId, chatId, messageId).catch(() => {});
    },
    []
  );

  const sendStreaming = useCallback<MessagesContextValue['sendStreaming']>(
    async (accountId, chatId, payload, options) => {
      // pending -- несозданный чат, который генерится по первому сообщению
      // берется токен, объявляется все что связано с текущим стэйтом
      const pendingKey = pendingKeyOf(accountId);
      const initialKey = chatId != null ? keyOf(accountId, chatId) : pendingKey;
      const bucket = stateRef.current[initialKey] ?? initialBucket;
      let activeKey = initialKey;
      let sessionMessages = bucket.data ?? [];

      // получившееся в итоге соо
      let final: CombinedMessage | null = null;
      // че упало
      let failureMessage: string | null = null;
      // отмена пользователем
      let wasCancelled = false;
      // заголовок чата
      let latestChatTitle: string | undefined;

      // если чат есть, то строка, нет -- null
      const initialChatId =
        chatId != null ? (typeof chatId === 'string' ? Number.parseInt(chatId, 10) : chatId) : null;
      // чем-то отличается от изначального. наверное тем, что при загрузке initial null, а этот не нал
      let resolvedChatId: number | null =
        initialChatId != null && !Number.isNaN(initialChatId) ? initialChatId : null;

      // если все те айдишники null - отправляется он
      const fallbackChatId = resolvedChatId ?? undefined;

      // объявление айдишника (если передан -- то temp, а иначе уже существующий)
      const tmpId = payload?.messageId == null ? `tmp-${Date.now()}` : payload.messageId;
      // заглушка для UI (загрузка сообщения)
      const placeholder: UiMessage = {
        id: String(tmpId),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        title: payload.content,
        isLoading: true,
      };

      // если пришел messageId, значит регенерация, иначе -- генерация с нуля
      if (payload?.messageId == null) {
        sessionMessages = [...sessionMessages, { ...placeholder }];
      } else {
        sessionMessages = sessionMessages.map(i => {
          if (i.id == String(payload?.messageId)) {
            return { ...i, isLoading: true };
          }
          return i;
        });
      }

      // статусы с бэка
      let statusTrail: StatusEntry[] = [...(bucket.statusTrail ?? [])];
      // последний статус
      let lastStatusSignature: string | null = null;
      // нужно ли очищать загрузочнцую штучку
      let pendingCleared = initialKey !== pendingKey;

      // новый чатик
      const onChatResolved = options?.onChatResolved;
      let chatResolutionEmitted = false;

      const emitChatResolved = (id: number, meta?: { title?: string }) => {
        if (chatResolutionEmitted) return;
        chatResolutionEmitted = true;
        onChatResolved?.(id, meta);
      };

      // сохранение состояния
      const emitState = () => {
        dispatch({
          type: 'set',
          key: activeKey,
          data: [...sessionMessages],
          statusTrail: [...statusTrail],
        });
      };

      // очистка загружаемого чата
      const clearPendingIfNeeded = () => {
        if (!pendingCleared && activeKey !== pendingKey) {
          pendingCleared = true;
          dispatch({ type: 'clear', key: pendingKey });
        }
      };

      // обновление загрузочного сообщения
      const updatePlaceholder = (updater: (msg: UiMessage) => UiMessage) => {
        let found = false;
        sessionMessages = sessionMessages.map(msg => {
          if (msg.id === tmpId) {
            found = true;
            return updater(msg);
          }
          return msg;
        });
        if (!found) {
          if (payload?.messageId == null) {
            sessionMessages = [...sessionMessages, updater({ ...placeholder })];
          } else {
            sessionMessages = sessionMessages.map(i => {
              if (i.id == String(payload?.messageId)) {
                return updater({ ...i });
              }
              return i;
            });
          }
        }
        emitState();
      };

      // замена заглушки на рил сообщение
      const replacePlaceholderWithMessages = (messages: UiMessage[]) => {
        const filtered = sessionMessages.filter(msg => msg.id !== tmpId);
        const existingIds = new Set(filtered.map(msg => msg.id));
        const appended = messages.filter(msg => !existingIds.has(msg.id));
        sessionMessages = [...filtered, ...appended];
        emitState();
      };

      // запись статуса
      const recordStatus = (status?: string, messageText?: string) => {
        const normalizedStatus = status?.trim();
        const normalizedMessage = (messageText ?? '').trim();
        const displayText = normalizedMessage || normalizedStatus || '';
        if (!displayText) return;

        const signature = `${normalizedStatus ?? ''}|${displayText}`;
        if (signature === lastStatusSignature) return;
        lastStatusSignature = signature;

        statusTrail = [
          ...statusTrail,
          {
            status: normalizedStatus,
            message: displayText,
            timestamp: Date.now(),
          },
        ];

        updatePlaceholder(msg => ({ ...msg, content: displayText, isLoading: true }));
      };

      // обновление ключа
      const promoteChatKey = (nextKey: string) => {
        if (activeKey === nextKey) return;
        activeKey = nextKey;
        emitState();
        clearPendingIfNeeded();
      };

      const syncChatFromEvent = (chatMeta?: StreamingChat) => {
        if (!chatMeta) return;
        // тут преобразование происходит id чата и id аккаунта в int
        const parsedChatIdRaw = chatMeta.id;
        const parsedChatId =
          typeof parsedChatIdRaw === 'string'
            ? Number.parseInt(parsedChatIdRaw, 10)
            : (parsedChatIdRaw ?? null);
        if (parsedChatId == null || Number.isNaN(parsedChatId)) return;

        resolvedChatId = parsedChatId;

        const parsedAccountIdRaw =
          chatMeta.account_id ??
          (typeof accountId === 'string' ? Number.parseInt(accountId, 10) : accountId);
        const parsedAccountId =
          typeof parsedAccountIdRaw === 'string'
            ? Number.parseInt(parsedAccountIdRaw, 10)
            : (parsedAccountIdRaw ?? null);

        if (parsedAccountId == null || Number.isNaN(parsedAccountId)) return;

        // загружаем title
        const title =
          typeof chatMeta.title === 'string' && chatMeta.title.trim().length > 0
            ? chatMeta.title
            : 'New chat';

        latestChatTitle = title;

        // пушим в чатики
        chats.upsert(accountId, {
          id: parsedChatId,
          title,
          account_id: parsedAccountId,
        });

        // обновляем временный (pending) ключ
        promoteChatKey(keyOf(accountId, parsedChatId));
        // очищаемся
        clearPendingIfNeeded();

        if (typeof parsedChatId === 'number' && !Number.isNaN(parsedChatId)) {
          // новосозданный чатик ифНидед
          emitChatResolved(parsedChatId, { title });
        }
      };

      const handleStreaming = (evt: StreamingEvent) => {
        if (!evt) return;
        const status = evt.status;
        const rawMessage = typeof evt.message === 'string' ? evt.message : '';
        const statusText = rawMessage.trim();

        // если чат не создан, то мы создаем новый чат из ивента
        syncChatFromEvent(evt.chat);

        if (statusText || status) {
          // записываем текущий статус
          recordStatus(status, statusText || status || '');
        }

        if (status === 'FAILED' || status === 'ERROR') {
          // все упало
          failureMessage = statusText || 'An error occurred while processing your request.';
          updatePlaceholder(msg => ({ ...msg, content: failureMessage!, isLoading: false }));
          return;
        }

        if (status !== 'COMPLETED' || !evt.response) {
          return;
        }
        const response = isCombinedMessageLike(evt.response) ? evt.response : null;
        if (!response) {
          return;
        }

        final = {
          id: response.id ?? Date.now(),
          chat_id: response.chat_id ?? resolvedChatId ?? fallbackChatId ?? undefined,
          sheet_id: response.sheet_id ?? payload.sheetId,
          user_request: response.user_request ?? payload.content,
          ai_reply: response.ai_reply ?? '',
          ai_reply_structured:
            (response as unknown as Record<string, unknown>).ai_reply_structured ?? null,
          created_at: response.created_at ?? new Date().toISOString(),
        } as CombinedMessage;
      };

      const handleSuccess = () => {
        const combined = final as CombinedMessage;
        const mapped = mapCombinedToUi([combined]).map(m => ({ ...m, isLoading: false }));
        const finalChatIdValue = combined.chat_id ?? resolvedChatId ?? chatId;
        const normalizedFinalChatId =
          typeof finalChatIdValue === 'string'
            ? Number.parseInt(finalChatIdValue, 10)
            : (finalChatIdValue ?? null);
        if (normalizedFinalChatId != null && !Number.isNaN(normalizedFinalChatId)) {
          resolvedChatId = normalizedFinalChatId;
          promoteChatKey(keyOf(accountId, normalizedFinalChatId));
          clearPendingIfNeeded();
        }

        replacePlaceholderWithMessages(mapped);

        if (resolvedChatId == null) {
          return;
        }

        const accountNumeric =
          typeof accountId === 'string' ? Number.parseInt(accountId, 10) : accountId;
        if (typeof accountNumeric !== 'number' || Number.isNaN(accountNumeric)) {
          return;
        }

        const metaTitle = typeof latestChatTitle === 'string' ? latestChatTitle : '';
        const normalizedMetaTitle = metaTitle.trim();
        const resolvedTitle = normalizedMetaTitle || mapped[0]?.title || 'New chat';
        chats.upsert(accountId, {
          id: resolvedChatId,
          title: resolvedTitle,
          account_id: accountNumeric,
        });
        emitChatResolved(resolvedChatId, { title: resolvedTitle });
      };

      const handleCancelled = () => {
        const latestStatus = statusTrail[statusTrail.length - 1]?.message ?? '';
        const fallbackContent = latestStatus || failureMessage || 'Generation cancelled by user.';
        wasCancelled = true;
        updatePlaceholder(msg => ({ ...msg, content: fallbackContent, isLoading: false }));
        clearPendingIfNeeded();
      };

      const handleFail = () => {
        const latestStatus = statusTrail[statusTrail.length - 1]?.message ?? '';
        const fallbackContent =
          failureMessage || latestStatus || 'An error occurred while processing your request.';
        updatePlaceholder(msg => ({ ...msg, content: fallbackContent, isLoading: false }));
      };

      // зачем-то сохранили состояние -_-
      emitState();

      try {
        const session = await messagesApi.createStreaming(
          accountId,
          chatId,
          {
            content: payload.content,
            sheet_id: payload.sheetId ?? undefined,
            category_ids: payload.categoryIds,
            selected_tag_ids: payload.categoryIds?.length ? undefined : payload.selectedTagIds,
            workflow: payload.workflow,
            chat_id: chatId ?? null,
            files: payload.files,
          },
          undefined,
          (evt: StreamingEvent) => handleStreaming(evt)
        );

        let cancelled = false;
        const activeHandle = {
          session,
          cancel: () => {
            if (cancelled) return;
            cancelled = true;
            wasCancelled = true;
            session.cancel();
          },
          isCancelled: () => cancelled,
        } as const;

        currentActiveStreamRef.current = activeHandle;

        const result = await session.done;

        if (result.aborted || activeHandle.isCancelled()) {
          handleCancelled();
        } else if (final) {
          handleSuccess();
        } else {
          throw Error();
        }
      } catch (e) {
        const isAbortError = (err: unknown) =>
          err instanceof DOMException ? err.name === 'AbortError' : false;
        const cancelled = wasCancelled || currentActiveStreamRef.current?.isCancelled() === true;
        if (cancelled || isAbortError(e)) {
          handleCancelled();
        } else {
          handleFail();
        }
      } finally {
        currentActiveStreamRef.current = null;
      }

      if (
        resolvedChatId != null &&
        typeof resolvedChatId === 'number' &&
        !Number.isNaN(resolvedChatId)
      ) {
        if (!wasCancelled) {
          emitChatResolved(resolvedChatId, { title: latestChatTitle });
        }
      }

      if (!wasCancelled) {
        clearPendingIfNeeded();
      }

      return resolvedChatId;
    },
    [chats]
  );

  const value = useMemo<MessagesContextValue>(
    () => ({
      state,
      load,
      setMessages,
      startRegenerate,
      sendStreaming,
      cancelStreaming: () => {
        const active = currentActiveStreamRef.current;
        if (active) {
          active.cancel();
        }
      },
    }),
    [state, load, setMessages, startRegenerate, sendStreaming]
  );

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
};
